'use-client';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@mybridge/ui/button';
import { Input } from '@mybridge/ui/input';

import {
  addEmail,
  emailList,
  getVerifyEmailOTP,
  removeEmail,
  sendMobileVerifyCode,
  setPrimaryEmail,
  verifyEmailOTP,
  verifyMobileOtp,
  clearVerificationState,
} from 'packages/mybridgev4/store/slices/settingsSlice.js';
import {
  getUserProfileInfo,
  userProfileAddInfo,
  updateUserDetails,
} from 'packages/mybridgev4/store/actions/user-profile.actions';

import Language from 'v4/snippets/settings/sections/Language';
import Name from 'v4/snippets/settings/sections/Name';
import Password from 'v4/snippets/settings/sections/Password';
// import Email from './sections/Email';
// import PhoneNumber from './sections/PhoneNumber';
import Location from 'v4/snippets/settings/sections/Location';
import Deactivation from 'v4/snippets/settings/sections/Deactivation';
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  HStack,
  Heading,
  Link,
  Stack,
  Text,
  useDisclosure,
  FormErrorMessage,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  AlertDialogCloseButton,
  Spinner,
  useToast,
} from '@mybridge/ui';
import {
  AddCircle,
  BackButton,
  CloseIcon,
  SimpleCloseIcon,
  EditIcon,
  TelephoneIcon,
  EmailIcon,
  BackArrow,
  BackArrowRTL,
} from '@mybridge/icons';
import styles from 'v4/snippets/settings/sections/settings.module.scss';

import {
  getCountries,
  getCountryCallingCode,
} from 'react-phone-number-input/input';
import en from 'react-phone-number-input/locale/en';
import _ from 'lodash';
import { AuthenticatedProviders } from 'v4/snippets/providers/authenticated';
import { useRouter } from 'next/router';
import { isMobile } from 'react-device-detect';
import Head from 'next/head';
import { IconButton } from '@mybridge/ui/icon-button';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { ANALYTICS_EVENTS, trackEvent } from 'v4/utils/analyticsEvents';
import { isIphoneWithSafari } from 'v4/utils/utils';

const Account = ({
  setSelectedComponent,
  setSelectedAccountComponent,
  setIsEditing,
  isEditing,
  setEditMode,
  editMode,
  setAddEditEmail,
  addEditEmail,
  setAddEditEmailOtp,
  addEditEmailOtp,
  isChangeNumber,
  setIsChangeNumber,
  setMobileOtp,
  mobileOtp,
  ...props
}) => {
  const [fields, setFields] = useState({});
  const { t } = useTranslator();
  const toast = useToast({
    position: 'top',
    isClosable: true,
    duration: 3000,
  });
  const { userProfileInfo, isProfileUpdated, loading } = useSelector(
    (state) => state.userProfile
  );
  const [iphoneWithSafari, setIphoneWithSafari] = useState(false);
  const { loggedIn, pageLoaded } = useSelector((state) => state.user);
  const { globalNotifCount } = useSelector((state) => state.notifications);
  const router = useRouter();
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);

  const { isEmailUpdated, emailInfo, receivedOtpCode, isMobileOtp } =
    useSelector((state) => state.settings);

  // const [addEditMobileNo, setAddEditMobileNo] = useState(false);

  const [errorFields, setErrorFields] = useState({});
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');

  const [otpError, setOtpError] = useState(false);
  const [otp, setOtp] = useState('');
  const [verifyEmailId, setVerifyEmailId] = useState('');
  const [showVerify, setShowVerify] = useState(false);
  const [country, setCountry] = useState('US');
  const [primaryEmailInfo, setPrimaryEmailInfo] = useState();
  const [isSmsAgreed, setIsSmsAgreed] = useState(false);

  const dispatch = useDispatch();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = React.useRef();
  const direction = useDirection();

  const primaryDisc = useDisclosure();
  const deleteDisc = useDisclosure();
  
  const trackAccountPageVisit = () => {
    const userSlug = userProfileInfo?.slug;
    if (!userSlug) return;
  
    trackEvent(ANALYTICS_EVENTS.AccountSettings, userSlug, {
      page_path: router.asPath,
      page_title: 'Accounts Settings',
      timestamp: new Date().toISOString()
    });
  };
  
  useEffect(() => {
    trackAccountPageVisit();
  }, []);

  useEffect(() => {
    dispatch(emailList());
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isEmailUpdated) {
      setAddEditEmail(false);
      setAddEditEmailOtp(false);
      dispatch(emailList());
    }
    // eslint-disable-next-line
  }, [isEmailUpdated]);

  useEffect(() => {
    if (receivedOtpCode) {
      setAddEditEmailOtp(true);
    }
  }, [receivedOtpCode]);

  useEffect(() => {
    if (isMobileOtp) {
      dispatch(getUserProfileInfo());
      // setAddEditMobileNo(false);
    }
  }, [isMobileOtp]);

  const fieldsOnChange = (e) => {
    const { name, value } = e.target;
    setFields((prevValues) => ({ ...prevValues, [name]: value }));
    if (errorFields?.[name]) {
      setErrorFields((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const phoneOnChange = (e) => {
    let name = 'changeNumber';
    const value = e.target.value;
    setFields((prevValues) => ({ ...prevValues, [name]: value }));
    if (errorFields?.changeNumber) {
      setErrorFields((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.changeNumber;
        return newErrors;
      });
    }
  };

  const resendVerification = (emailId) => {
    setOtp('');
    setVerifyEmailId(emailId);
    dispatch(getVerifyEmailOTP({ email: emailId }));
  };

  const handleSetPrimaryEmail = (emailId) => {
    dispatch(setPrimaryEmail({ email: emailId })).then(() => {
      dispatch(getUserProfileInfo());
      primaryDisc.onClose();
    });
  };

  const deleteEmail = (emailId) => {
    // if (
    //   confirm(
    //     `Are you sure you want to delete this email address: ${emailId}`
    //   ) == true
    // ) {
    dispatch(removeEmail({ email: emailId }));
    deleteDisc.onClose();
    // }
  };

  const editEmail = () => {
    setAddEditEmail(true);
    setFields({ ...userProfileInfo });
    setIsEditing('email');
    setSelectedAccountComponent('email');
  };

  const editMobileNo = () => {
    // setAddEditMobileNo(true);
    setIsChangeNumber(true);
    setShowVerify(false);
    setMobileOtp(false);
    setIsEditing('phone');
    setSelectedAccountComponent('phone');
    setIsSmsAgreed(false);
  };

  const cancelChangeNumber = () => {
    // setAddEditMobileNo(false);
    setIsChangeNumber(false);
    setIsEditing('');
    setSelectedAccountComponent('account');
    setIsSmsAgreed(false);
  };

  const saveNewMobileNumber = () => {
    let errorObject = {};

    if (!fields.changeNumber) {
      errorObject.changeNumber = t('settingAccount.enterNewMobile') || 'Please enter a new mobile number';
    }
    const phoneNumber =
      `+` + getCountryCallingCode(country) + fields.changeNumber;
    if (Object.keys(errorObject).length > 0) {
      setErrorFields(errorObject);
      return;
    } else {
      setErrorFields({});
    }
    const formData = new FormData();
    // formData.append('mobile_number', fields.changeNumber);
    dispatch(
      updateUserDetails({
        mobile_number: fields.changeNumber,
        iso_code: `+${getCountryCallingCode(country)}`,
      })
    ).then((response) => {
      if (response?.payload?.error || response?.error || response?.meta?.requestStatus === 'rejected') {
        setErrorFields({
          changeNumber: response?.payload?.error || response?.payload?.detail || response?.payload?.message || t('settingAccount.updateFailed') || 'Failed to update mobile number',
        });
        return;
      }
      setIsChangeNumber(false);
      handleSendOtpCode();
      setShowVerify(true);
      setIsChangeNumber(false);
      setMobileOtp(true);
      setSelectedAccountComponent('otp')
    });
  };

  const handleSendOtpCode = (verify=false) => {
    // let errorObject = {}

    // if (!fields.verfiyNumber) {
    //     errorObject.verfiyNumber = 'Please enter a mobile number'
    // }

    // if (Object.keys(errorObject).length > 0) {
    //     setErrorFields(errorObject)
    //     return
    // } else {
    //     setErrorFields({})
    // }

    if (verify) {
      dispatch(sendMobileVerifyCode()).then((response) => {
        if (response?.error) {
          const errMsg = response?.error?.message || t('settingAccount.sendOtpFailed') || 'Failed to send verification code';
          toast({
            title: errMsg,
            status: 'error',
          });
        } else {
          toast({
            title: 'Verification code sent successfully',
            status: 'success',
          });
        }
      });
    }
    // setAddEditMobileNo(true);
    setShowVerify(true);
    setIsChangeNumber(false);
    setMobileOtp(true);
    setSelectedAccountComponent('otp');
  };

  const handleVerifyMobileOtp = () => {
    let errorObject = {};

    if (!fields.mobileOtp || fields.mobileOtp.length <= 0) {
      errorObject.mobileOtp = t('settingAccount.validOTP') ||'Please enter a valid OTP';
    }

    if (Object.keys(errorObject).length > 0) {
      setErrorFields(errorObject);
      return;
    } else {
      setErrorFields({});
    }
    dispatch(
      verifyMobileOtp({
        otp: fields.mobileOtp,
      })
    ).then((response) => {
      if (response?.payload?.error || response?.payload?.detail || response?.error || response?.meta?.requestStatus === 'rejected') {
        setErrorFields({
          mobileOtp: response?.payload?.detail || response?.payload?.error || response?.payload?.message || t('settingAccount.otpVerifyFailed') || 'Verification failed. Please try again.',
        });
        return;
      }
      setMobileOtp(false);
      setIsEditing('');
      setSelectedAccountComponent('account');
    });
  };

  const handleVerifyMobileNo = () => {
    // setAddEditMobileNo(true);
    setShowVerify(true);
    setMobileOtp(false);
    setIsChangeNumber(false);
  };

  const cancelVerifyPhoneNo = () => {
    // setAddEditMobileNo(false);
    setShowVerify(false);
  };

  useEffect(() => {
    if (isProfileUpdated) {
      dispatch(getUserProfileInfo());
      // setAddEditMobileNo(false);
    }
    // eslint-disable-next-line
  }, [isProfileUpdated]);

  const handleEmail = () => {
    const emailVerify =
      /^([a-z\d\.\+-]+)@([a-z\d-]+)\.([a-z]{2,8})(\.[a-z]{2,8})?$/;
    if (!email || !emailVerify.test(email)) {
      setEmailError(t('settingAccount.emailRequired') || 'Enter a valid email address');
      return;
    }
    try {
      setVerifyEmailId(email);
      // eslint-disable-next-line no-undef
      dispatch(addEmail({ email })).then((response) => {
        if (!response.payload.status_code) {
          dispatch(getVerifyEmailOTP({ email: email })).then((response) => {
            if (response) {
              setAddEditEmail(false);
              setEmail('');
              setEmailError('');
            }
          });
        } else {
          setEmailError(response.payload.email);
          setVerifyEmailId('');
          setAddEditEmail(true);
          return;
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  const verifyEmailOtpCode = () => {
    if (!otp) {
      setOtpError('Enter a OTP');
      return;
    }
    dispatch(verifyEmailOTP({ email: verifyEmailId, otp })).then((response) => {
      if (!response.payload.status_code) {
        setAddEditEmail(false);
        setEmail('');
        setEmailError('');
        setVerifyEmailId('');
        setAddEditEmailOtp(false);
        dispatch(clearVerificationState());
      } else {
        setOtpError(response.payload.detail);
        setAddEditEmailOtp(true);
      }
    });
  };

  const phoneWithCode = (num) => {
    if (num?.length > 10) {
      const code = num?.substr?.(0, num.length - 10);
      const phone = num?.substr?.(num.length - 10);
      return (
        <Box as="span">
          +{code.replace('-', '')}
          <Box as="span" className={styles.seperator}></Box>
          {phone}
        </Box>
      );
    }
    return num;
  };

  const CountrySelect = ({ value, onChange, labels, ...rest }) => (
    <select
      {...rest}
      value={value}
      onChange={(event) => onChange(event.target.value || undefined)}
    >
      <option value="">{t('settingAccount.selectCountry') || "Country"}</option>
      {getCountries().map((country) => (
        <option key={country} value={country}>
          {labels[country]} +{getCountryCallingCode(country)}
        </option>
      ))}
    </select>
  );

  const sortedEmails = _.orderBy(emailInfo?.results, ['is_primary'], ['desc']);
  const primaryEmail = sortedEmails.find((email) => email.is_primary);
  const nonPrimaryEmails = sortedEmails.filter((email) => !email.is_primary);

  const handleMarkPrimaryEmail = (emailId) => {
    setPrimaryEmailInfo(emailId);
    primaryDisc.onOpen();
  };
  const handleDeleteEmail = (emailId) => {
    setPrimaryEmailInfo(emailId);
    deleteDisc.onOpen();
  };

  useEffect(() => {
    if (isIphoneWithSafari()) {
      setIphoneWithSafari(true);
      window.scrollTo(0, 0);
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100dvh';
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100dvh';
    } else {
      setIphoneWithSafari(false);
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
    };
  }, []);

  const containerRef = React.useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    if (isIphoneWithSafari()) {
      window.scrollTo(0, 0);
    }
  }, [isEditing]);

  return (
    <AuthenticatedProviders>
      <Head>
        <title>
          {loggedIn && globalNotifCount ? `(${globalNotifCount})` : ''} {t('otherModules.settings') || "Settings"}
          - {t('setting.account') || "Account"} | myBridge.me
        </title>
      </Head>
      <Box
        ref={containerRef}
        h={iphoneWithSafari ? 'calc(100dvh - 170px)' : undefined}
        overflowY={iphoneWithSafari ? 'auto' : undefined}
        w="100%"
      >
        {isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && (
          <Name
            setIsEditing={setIsEditing}
            setSelectedAccountComponent={setSelectedAccountComponent}
            setEditMode={setEditMode}
            editMode={editMode}
          />
        )}
      {!isMobile && isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && <hr className="hr" />}

      {isEditing !== 'name' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && (
          <Password
            setIsEditing={setIsEditing}
            setSelectedAccountComponent={setSelectedAccountComponent}
            setEditMode={setEditMode}
            editMode={editMode}
          />
        )}
      {!isMobile && isEditing !== 'name' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && <hr className="hr" />}

      {isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'location' && (
          <Language
            setIsEditing={setIsEditing}
            setSelectedAccountComponent={setSelectedAccountComponent}
            setEditMode={setEditMode}
            editMode={editMode}
          />
        )}
      {!isMobile && isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'location' && <hr className="hr" />}

      {isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && (
          <Box py={["10px","15px"]}>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              p={['10px', '0']}
            >
              {isMobile && !addEditEmail && (
                /* <Box w={['80%', '350px']}> */
                <Box flex={1} maxW="350px" mr={4} minW="0">
                  {isMobile && !addEditEmail && (
                    <>
                      <Box mt={3}>
                        {primaryEmail && ( // Check if primaryEmail exists
                          /* <Box mt={3} display="flex" alignItems="center"> */
                          <Box mt={3} display="flex" alignItems="center" minW="0">
                            <EmailIcon
                              width="24"
                              height="24"
                              style={{ marginRight: '10px' }}
                              color="#868585"
                            />
                            <Text
                              color="#868585"
                              width="90%" // Set the width to 90%
                              whiteSpace="nowrap" // Prevent text from wrapping to the next line
                              overflow="hidden" // Hide overflow text
                              textOverflow="ellipsis"
                            >
                              {primaryEmail.email}
                            </Text>
                          </Box>
                        )}
                        {primaryEmail?.is_primary && ( // Check if primaryEmail exists and is primary
                          <Text color="#868585" fontSize={['12px']} ml="35px">
                            {t('settingAccount.primary') || "Primary"}
                          </Text>
                        )}
                        {nonPrimaryEmails.map((email, index) => (
                          /* <Box mt={3} display="flex" alignItems="center"> */
                          <Box mt={3} display="flex" alignItems="center" minW="0">
                          <Text
                            color="#868585"
                            width="90%" // Set the width to 90%
                            whiteSpace="nowrap" // Prevent text from wrapping to the next line
                            overflow="hidden" // Hide overflow text
                            textOverflow="ellipsis"
                            ml="35px"
                          >
                            {email.email}
                          </Text>
                        </Box>
                      ))}
                      <Text color="#868585" fontSize={['12px']} ml="35px">
                        {t('profileDetails.email') || "Email"}
                      </Text>
                    </Box>
                  </>
                )}
              </Box>)}
              {!isMobile && (
                <HStack>
                  {(addEditEmail) && <Button
                    variant="transparent"
                    leftIcon={direction === 'rtl' ? <BackArrowRTL color="#868585" /> : <BackArrow color="#868585" />}
                    color="#868585"
                    onClick={() => {
                      setAddEditEmail(false);
                      setEmail('');
                      setEmailError('');
                      setIsEditing('');
                      setSelectedAccountComponent('account');
                    }}
                    p={0}
                  />} 
                  {/* <Text>{t('settingAccount.emailAdded') || "Email address you have added"}</Text> */}
                  <Text fontSize={['16px', '14px', '14px', '16px']}>{t('settingAccount.emailAdded') || "Email address you have added"}</Text>
                </HStack>
              )}
              {isMobile && !addEditEmail ? (
                <IconButton bg="none" onClick={editEmail} icon={<EditIcon />} />
              ) : (
                !addEditEmail && (
                  <Button
                    variant="ghost"
                    borderRadius={"30px"}
                    color="black"
                    onClick={editEmail}
                    leftIcon={<AddCircle width={20} height={20} />}
                    fontSize={['14px', '13px', '13px', '14px']}
                  >
                    {t('profileDetails.addEmail') || 'Add Email'}
                  </Button>
                )
              )}
            </Flex>
            {addEditEmail && (
              <>
                <Stack
                  spacing="2"
                  my={[null, "10px"]}
                  w={['100%', '350px']}
                  p={['10px', '0']}
                >
                  {isMobile && (
                  <Box mb={2}>
                    <Text color="brandGray.700" fontSize={['20px', '16px']}>
                      {t('settingAccount.emailAdded') || "Email address you have added"}
                    </Text>
                    <Text
                      fontSize={['md', 'sm']}
                      color="brandGray.700"
                      mt="10px"
                    >
                      {t('settingAccount.addEmail') || "Add an email address"}
                    </Text>
                  </Box>
                )}
                  <FormControl isInvalid={emailError}>
                    <Input
                      type="text"
                      placeholder={t('settingAccount.addNewEmail') || "Add New Email Address"}
                      value={email}
                      // onChange={(e) => setEmail(e.target.value)}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError('');
                      }}
                      name="email"
                      className={isMobile ? styles.dataDisplayBoxes : undefined}
                      variant={isMobile ? undefined : 'greyRounded'}
                    />
                    {emailError && (
                      <FormErrorMessage className="text-danger">
                        {emailError}
                      </FormErrorMessage>
                    )}
                  </FormControl>
                  <Flex
                    justifyContent={isMobile ? 'flex-end' : 'flex-start'}
                    columnGap="2"
                    mt={['5%', '2%']}
                  >
                    <Button
                      onClick={() => {
                        setAddEditEmail(false);
                        setEmail('');
                        setEmailError('');
                        setIsEditing('');
                        setSelectedAccountComponent('account');
                      }}
                      borderRadius={['30px', '30px']}
                      bg="#E4E6EB"
                    >
                      {t('buttons.cancel') || "Cancel"}
                    </Button>
                    <Button
                      onClick={handleEmail}
                      color="#fff"
                      bg={['#3D5A80', '#3D5A80']}
                      borderRadius={['30px', '30px']}
                    >
                      {t('buttons.save') || "Save"}
                    </Button>
                  </Flex>
                  <Box mt={3}>{!isMobile && <hr />}</Box>
                </Stack>
              </>
            )}
            {/* OTP fields for verification */}
            {addEditEmailOtp && (
              <Stack spacing="2" w={['100%', '350px']} p={['10px', '0']} my={2}>
                <Text color="brandGray.700" fontSize="sm">
                  {t('settingAccount.verifyOTP') || "Verify OTP for"} {verifyEmailId}
                </Text>
                <Input
                  name="otp"
                  value={otp}
                  // onChange={(e) => setOtp(e.target.value)}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    if (otpError) setOtpError('');
                  }}
                  placeholder={t('settingAccount.enterEmailOTP') || "Enter Email OTP"}
                  variant="greyRounded"
                />
                {otpError && <Box className="text-danger">{otpError}</Box>}
                <Flex
                  justifyContent={isMobile ? 'flex-end' : 'space-between'}
                  columnGap="2"
                  mt={['5%', '2%']}
                >
                  <Button
                    className="btn btn-secondary fw-normal"
                    onClick={() => {
                      setAddEditEmailOtp(false);
                      setIsEditing('');
                      setSelectedAccountComponent('account');
                    }}
                    borderRadius={['30px', '30px']}
                    bg="#E4E6EB"
                  >
                    {t('buttons.cancel') || "Cancel"}
                  </Button>
                  <Button
                    disabled={loading}
                    onClick={verifyEmailOtpCode}
                    color="#fff"
                    bg={['#3D5A80', '#3D5A80']}
                    borderRadius={['30px', '30px']}
                  >
                    {loading ? <Spinner /> : t('settingAccount.verify') || 'Verify'}
                  </Button>
                </Flex>

                <Box mt={3}>
                  <hr />
                </Box>
              </Stack>
            )}

            {/* Email ID list */}
            {emailInfo?.results?.length > 0 && !isMobile && (
              <>
                {sortedEmails?.map(({ email, is_primary, is_verified }) => (
                  <Flex
                    key={email}
                    justifyContent="space-between"
                    alignItems="center"
                    py="5px"
                    w={['100%', 'auto']}
                    p={['10px', '0 0 10px']}
                  >
                    <Stack
                      alignItems={['flex-start', 'center']}
                      spacing={3}
                      direction={['column', 'row']}
                      width="100%"
                      justifyContent="flex-start"
                    >
                      <Box
                        className={styles.dataDisplayBoxes}
                        display="flex"
                        gap={3}
                        alignItems="center"
                      >
                        {/* <Text w={'100%'} textOverflow="ellipsis"> */}
                        <Text w={'100%'} textOverflow="ellipsis" fontSize={['16px', '13px', '13px', '16px']}>
                          {email}
                        </Text>
                        {!is_primary && (
                          <SimpleCloseIcon
                            cursor="pointer"
                            onClick={() => handleDeleteEmail(email)}
                            width={15}
                            height={15}
                          />
                        )}
                      </Box>
                      {is_primary && (
                        /* <Text color="brandGreen.500" p={['0 18px', '0']}> */
                        <Text color="brandGreen.500" p={['0 18px', '0']} fontSize={['16px', '13px', '13px', '16px']}>
                          {t('settingAccount.primary') || "Primary"}
                        </Text>
                      )}
                      {!is_primary && is_verified && (
                        <Button
                          variant="transparent"
                          color="brandPrimary.500"
                          onClick={() => {
                            handleMarkPrimaryEmail(email);
                          }}
                          fontSize={['14px', '13px', '13px', '14px']}
                        >
                          {t('settingAccount.makePrimary') || "Make Primary"}
                        </Button>
                      )}
                      {!is_verified && (
                        <Button
                          variant="transparent"
                          color="brandPrimary.500"
                          onClick={() => resendVerification(email)}
                          fontSize={['14px', '13px', '13px', '14px']}
                        >
                          {t('settingAccount.verify') || "Verify"}
                        </Button>
                      )}
                    </Stack>
                  </Flex>
                ))}
              </>
            )}
          </Box>
        )}
      {!isMobile && isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && <hr className="hr" />}

      {/* Phone Number */}
      {isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && (
          <Box>
            <Flex
              justifyContent="space-between"
              alignItems="center"
              spacing={0}
              p={['8px', '0']}
            >
              {/* <Box w={['100%', '350px']}> */}
              <Box flex={1} maxW="350px" mr={4} minW="0">
                {isMobile && !isChangeNumber && !mobileOtp && (
                   <>
                     {/* <Box mt={3} display="flex" alignItems="center"> */}
                     <Box mt={3} display="flex" alignItems="center" minW="0">
                      <TelephoneIcon
                        width="24"
                        height="24"
                        style={{ marginRight: '10px' }}
                        color="#868585"
                      />
                      <Text
                        color="#868585"
                        width="90%" // Set the width to 90%
                        whiteSpace="nowrap" // Prevent text from wrapping to the next line
                        overflow="hidden" // Hide overflow text
                        textOverflow="ellipsis"
                      >
                        {userProfileInfo?.iso_code}{' '}
                        {userProfileInfo?.mobile_number}
                      </Text>
                    </Box>
                    <Text color="#868585" fontSize={['12px']} ml="35px">
                      {t('profileDetails.mobileNumber') || 'Mobile Number'}
                    </Text>
                  </>
                )}
                {!isMobile && (
                  <HStack flex="1">
                    {(isChangeNumber) && <Button
                      variant="transparent"
                      leftIcon={direction === 'rtl' ? <BackArrowRTL color="#868585" /> : <BackArrow color="#868585" />}
                      color="#868585"
                      onClick={cancelChangeNumber}
                      p={0}
                    />}
                    {/* <Text color="brandGray.700">{t('myCalendar.phoneNumber') || "Phone Number"}</Text> */}
                    <Text color="brandGray.700" fontSize={['16px', '14px', '14px', '16px']}>{t('myCalendar.phoneNumber') || "Phone Number"}</Text>
                  </HStack>
                )}
                {isMobile && isChangeNumber && (
                  <>
                    <Text color="brandGray.700" fontSize={['20px', '16px']}>
                      {t('myCalendar.phoneNumber') || "Phone Number"}
                    </Text>
                    <Text
                      fontSize={['md', 'sm']}
                      color="brandGray.700"
                      mt="10px"
                    >
                      {t('settingAccount.addTroubleSigning') || "Add a phone number in case you have trouble signing in"}
                    </Text>
                  </>
                )}
                {isMobile && mobileOtp && (
                  <>
                    <Text color="brandGray.700" fontSize={['20px', '16px']}>
                      OTP
                    </Text>
                    <Text fontSize="sm" color="brandGray.700" mt={[0, '10px']}>
                      {t('settingAccount.verifyCode') || "Enter the verification code we sent to the number"}
                    </Text>
                  </>
                )}
              </Box>
              {isMobile && !isChangeNumber ? (
                <IconButton
                  bg="none"
                  onClick={editMobileNo}
                  icon={<EditIcon />}
                />
              ) : (
                !isChangeNumber && (
                  <HStack gap={2}>
                    <Button
                      borderRadius={"30px"}
                      variant="ghost"
                      color="black"
                      onClick={editMobileNo}
                      leftIcon={<AddCircle width={20} height={20} />}
                      fontSize={['14px', '13px', '13px', '14px']}
                    >
                      {t('settingAccount.editPhone') || "Edit Phone"}
                    </Button>
                  </HStack>
                )
              )}
            </Flex>
            {!mobileOtp && !isMobile && (
              <Text
                fontSize="sm"
                color="brandGray.700"
                mt={['0', '10px']}
                p={['10px', '0']}
              >
                {t('settingAccount.editPhone') || "Add a phone number to help you sign in if there is a problem."}
              </Text>
            )}

            {userProfileInfo?.mobile_number && !isMobile && (
              <Flex
                key={email}
                justifyContent="space-between"
                alignItems="center"
                py="5px"
              >
                <Stack
                  direction={['column', 'row']}
                  alignItems="center"
                  gap={3}
                >
                  <Box className={styles.dataDisplayBoxes}>
                    {userProfileInfo?.iso_code} {userProfileInfo?.mobile_number}
                  </Box>
                  {!userProfileInfo.is_mobile_verified && (
                    <Button
                      variant="transparent"
                      color="brandPrimary.500"
                      onClick={handleSendOtpCode}
                      fontSize={['14px', '13px', '13px', '14px']}
                    >
                      {t('settingAccount.verifyMobile') || "Verify Mobile Number"}
                    </Button>
                  )}
                </Stack>
              </Flex>
            )}

            {isChangeNumber && (
              <Box w={['100%', '50%']}>
                <Stack spacing="4" my="10px" p={['0 10px', 0]}>
                  <Flex className={styles.displayBoxes}>
                    <CountrySelect
                      labels={en}
                      value={country}
                      onChange={setCountry}
                    />
                    <Input
                      value={fields.changeNumber}
                      onChange={phoneOnChange}
                      name="changeNumber"
                      placeholder={t('myCalendar.phoneNumber') || "Phone Number"}
                      variant="transparent"
                      border="none"
                    />
                  </Flex>
                  {errorFields?.changeNumber && (
                    <Text color="red.500" fontSize="sm" mt="-2">
                      {errorFields.changeNumber}
                    </Text>
                  )}
                  <Flex
                    justifyContent={isMobile ? 'flex-end' : 'space-between'}
                    columnGap="2"
                    mt={['5%', '2%']}
                  >
                    <Button
                      onClick={cancelChangeNumber}
                      borderRadius={['30px', '30px']}
                      bg="#E4E6EB"
                    >
                      {t('buttons.cancel') || "Cancel"}
                    </Button>
                    <Button
                      isDisabled={!isSmsAgreed || !fields?.changeNumber || loading}
                      onClick={saveNewMobileNumber}
                      color="#fff"
                      bg={['#3D5A80', '#3D5A80']}
                      borderRadius={['30px', '30px']}
                    >
                      {isChangeNumber && loading ? <Spinner /> : t('settingAccount.sendCode') ||'Send code'}
                    </Button>
                  </Flex>
                </Stack>           
                {!isMobile && (
                  <Box my={3}>
                    {/* <hr /> */}
                  </Box>
                )}
              </Box>
            )}

            {mobileOtp && (
              <Box w={['100%', '350px']} p={['0 10px', 0]}>
                {!isMobile && (
                  <Text fontSize="sm" color="brandGray.700" mt={[0, '10px']}>
                    {t('settingAccount.verifyCode') || "Enter the verification code we sent to the number"}
                  </Text>
                )}
                <Flex alignItems="center" gap="4" mt="10px">
                  <Box>
                    <Input
                      type="number"
                      name="mobileOtp"
                      value={fields.mobileOtp}
                      onChange={fieldsOnChange}
                      placeholder={t('settingAccount.enterOTP') || "Enter OTP"}
                      className={isMobile ? styles.dataDisplayBoxes : undefined}
                      variant={isMobile ? undefined : 'greyRounded'}
                    />
                  </Box>
                  <Box>
                    <Button
                      size="sm"
                      color={['#fff', '#000']}
                      borderRadius={['30px', '30px']}
                      bg={['#3D5A80', '#EDF2F7']}
                      onClick={()=>handleSendOtpCode(true)}
                    >
                      {t('settingAccount.resendCode') || "Resend Code"}
                    </Button>
                  </Box>
                </Flex>
                {errorFields.mobileOtp && (
                  <Text color="red.500" fontSize="sm" mt="1">
                    {errorFields.mobileOtp}
                  </Text>
                )}
                <Flex
                  justifyContent={isMobile ? 'flex-end' : 'space-between'}
                  columnGap="2"
                  mt={['5%', '2%']}
                >
                  <Button
                    onClick={() => {
                      setMobileOtp(false);
                      setSelectedAccountComponent('email');
                    }}
                    borderRadius={['30px', '30px']}
                    bg="#E4E6EB"
                  >
                    {t('buttons.cancel') || "Cancel"}
                  </Button>
                  <Button
                    disabled={loading}
                    onClick={handleVerifyMobileOtp}
                    color="#fff"
                    bg={['#3D5A80', '#3D5A80']}
                    borderRadius={['30px', '30px']}
                  >
                    {loading && mobileOtp ? <Spinner /> : t('settingAccount.verify') || 'Verify'}
                  </Button>
                </Flex>
                {!isMobile && (
                  <Box my={3}>
                    <hr />
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      {!isMobile && isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && <hr className="hr" />}
      {isChangeNumber && (
        <Box px={['10px', 0]} pb="10px">
          
          <Flex alignItems="flex-start" gap="2" mt="3">
            <Checkbox
              isChecked={isSmsAgreed}
              onChange={(e) => setIsSmsAgreed(e.target.checked)}
              mt="1"
            />
            <Text fontSize={["xs", "sm"]} color="brandGray.700">
              {`By entering your mobile number and clicking "Send Code," you agree to receive one-time SMS verification codes from `}<Link display="inline" href="https://mybridge.me" target="_blank">myBridge.me</Link>{` for identity verification and authentication. Message frequency varies. Message and data rates may apply. Reply STOP to opt out. Reply HELP for help.`}
            </Text>
          </Flex>
        </Box>
      )}
      {/* Location */}
      {isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' 
        && (
          <Location
          setEditMode={setEditMode}
          editMode={editMode}
            setIsEditing={setIsEditing}
            setSelectedAccountComponent={setSelectedAccountComponent}
          />
        )}
      {!isMobile && isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' 
        && <hr className="hr" />}

      {/* Deactivate Account */}
      {isEditing !== 'name' &&
        isEditing !== 'password' &&
        isEditing !== 'email' &&
        isEditing !== 'phone' &&
        isEditing !== 'language' &&
        isEditing !== 'location' && <Deactivation />}
      {primaryEmailInfo && (
        <AlertDialog {...primaryDisc} width="600px" isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t('settingAccount.makeEmailPrimary') || "Set Email as primary"}
              </AlertDialogHeader>

              <AlertDialogBody>
                {`${t('settingAccount.confirmPrimaryEmail') || "Are you sure? Are you sure you want to change the primary email address to:"} ${primaryEmailInfo}.`}
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button onClick={() => primaryDisc.onClose()}>{t('buttons.cancel') || "Cancel"}</Button>
                <Button
                  variant="primary"
                  onClick={() => handleSetPrimaryEmail(primaryEmailInfo)}
                  ml={3}
                >
                  {t('settingAccount.makePrimary') || "Make Primary"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}

      {primaryEmailInfo && (
        <AlertDialog {...deleteDisc} width="600px" isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                {t('settingAccount.makeEmailPrimary') || "Set Email as primary"}
              </AlertDialogHeader>

              <AlertDialogBody>
                {`${t('settingAccount.confirmDeleteEmail') || "Are you sure you want to delete this email address:"} ${primaryEmailInfo}`}
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button onClick={() => deleteDisc.onClose()}>{t('buttons.cancel') || "Cancel"}</Button>
                <Button
                  bg="brandRed.500"
                  onClick={() => deleteEmail(primaryEmailInfo)}
                  ml={3}
                  color="white"
                >
                  {t('buttons.delete') || "Delete"}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
      </Box>
    </AuthenticatedProviders>
  );
};

export default Account;
